import { useCallback } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { useCachedQuery } from "./useCachedQuery";
import type { ResourceCategory, ResourceFormat, ResourceView, ResourcesStatus } from "./useResources";

export type AdminResourceWrite = {
  slug: string | undefined;
  title: string;
  description: string | null;
  category: ResourceCategory;
  format: ResourceFormat;
  videoUrl: string | null;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
  bodyMarkdown: string | null;
  integrationSlug: string | null;
  published: boolean;
  sortOrder: number;
};

export type ResourceErrorCode =
  | "NOT_FOUND"
  | "SLUG_CONFLICT"
  | "INVALID_VIDEO_URL"
  | "INVALID"
  | "UNAUTHORIZED"
  | "UNKNOWN";

export type ResourceResult<T> =
  | { ok: true; value: T }
  | { ok: false; code: ResourceErrorCode; message: string };

export type UseAdminResourcesResult = {
  status: ResourcesStatus;
  resources: ReadonlyArray<ResourceView>;
  error: string | null;
  refresh: () => Promise<void>;
  create: (write: AdminResourceWrite) => Promise<ResourceResult<ResourceView>>;
  update: (id: string, write: AdminResourceWrite) => Promise<ResourceResult<ResourceView>>;
  remove: (id: string) => Promise<ResourceResult<void>>;
};

const ADMIN_URL = "/api/admin/resources";

const KNOWN_CODES: ReadonlyArray<ResourceErrorCode> = [
  "NOT_FOUND",
  "SLUG_CONFLICT",
  "INVALID_VIDEO_URL",
  "INVALID",
  "UNAUTHORIZED",
  "UNKNOWN",
];

const narrowCode = (code: string): ResourceErrorCode =>
  (KNOWN_CODES as ReadonlyArray<string>).includes(code) ? (code as ResourceErrorCode) : "UNKNOWN";

type ServerError = { code: string; message: string };

const parseError = async (res: Response, fallback: string): Promise<{ code: ResourceErrorCode; message: string }> => {
  if (res.status === 401) {
    return { code: "UNAUTHORIZED", message: "Session expired. Refresh to sign in again." };
  }
  const body = (await res.json().catch(() => null)) as ServerError | null;
  return {
    code: narrowCode(body?.code ?? "UNKNOWN"),
    message: body?.message ?? `${fallback} (${res.status})`,
  };
};

const fetchResources = async (signal: AbortSignal): Promise<ReadonlyArray<ResourceView>> => {
  const res = await fetch(ADMIN_URL, { credentials: "include", signal });
  if (res.status !== 200) throw new Error(`List failed (${res.status})`);
  return (await res.json()) as ResourceView[];
};

export const useAdminResources = (): UseAdminResourcesResult => {
  const { status, data, error, refetch, mutate } = useCachedQuery<ReadonlyArray<ResourceView>>({
    cacheResource: ADMIN_QUERIES.resources.resource,
    queryKey: ADMIN_URL,
    fetcher: fetchResources,
  });
  const resources = data ?? [];
  const refresh = refetch;

  const create = useCallback(
    async (write: AdminResourceWrite): Promise<ResourceResult<ResourceView>> => {
      try {
        const res = await fetch(ADMIN_URL, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify(write),
        });
        if (res.status === 201) {
          const created = (await res.json()) as ResourceView;
          mutate((prev) => [created, ...prev]);
          return { ok: true, value: created };
        }
        const err = await parseError(res, "Create failed");
        return { ok: false, ...err };
      } catch (err) {
        return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : String(err) };
      }
    },
    [mutate],
  );

  const update = useCallback(
    async (id: string, write: AdminResourceWrite): Promise<ResourceResult<ResourceView>> => {
      try {
        const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(id)}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify(write),
        });
        if (res.status === 200) {
          const updated = (await res.json()) as ResourceView;
          mutate((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
          return { ok: true, value: updated };
        }
        const err = await parseError(res, "Update failed");
        return { ok: false, ...err };
      } catch (err) {
        return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : String(err) };
      }
    },
    [mutate],
  );

  const remove = useCallback(
    async (id: string): Promise<ResourceResult<void>> => {
      try {
        const res = await fetch(`${ADMIN_URL}/${encodeURIComponent(id)}`, {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (res.status === 204) {
          mutate((prev) => prev.filter((r) => r.id !== id));
          return { ok: true, value: undefined };
        }
        const err = await parseError(res, "Delete failed");
        return { ok: false, ...err };
      } catch (err) {
        return { ok: false, code: "UNKNOWN", message: err instanceof Error ? err.message : String(err) };
      }
    },
    [mutate],
  );

  return { status, resources, error, refresh, create, update, remove };
};
